import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Link as MuiLink,
  Skeleton,
  TableCell,
  TableRow,
  Typography,
} from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { Link, useParams } from "react-router-dom";

import { supportApi } from "../client.js";
import { SupportErrorState } from "../components/SupportEmptyState.js";
import { StatusBadge } from "../components/StatusBadge.js";
import { SupportTable } from "../components/SupportTable.js";
import { BackButton } from "@/components/ui/back-button";

/**
 * Single organisation detail. Shows the org metadata, the contact list,
 * and the recent ticket history. Three queries fire in parallel —
 * react-query's fetcher pool keeps them coordinated. Each card collapses
 * into its own empty/error state without taking the whole page down.
 */
export function OrganizationDetailPage(): JSX.Element {
  const { id } = useParams<{ id: string }>();

  const orgQuery = useQuery({
    queryKey: ["support", "org", id],
    queryFn: () => supportApi.getOrganization(id!),
    enabled: !!id,
  });
  const contactsQuery = useQuery({
    queryKey: ["support", "org", id, "contacts"],
    queryFn: () => supportApi.listOrgContacts(id!),
    enabled: !!id,
  });
  const ticketsQuery = useQuery({
    queryKey: ["support", "org", id, "tickets"],
    queryFn: () => supportApi.listOrgTickets(id!, { per_page: "10" }),
    enabled: !!id,
  });

  const o = orgQuery.data?.data;

  return (
    <Box sx={{ display: "grid", gap: 1.5 }}>
      <BackButton to="/support/organizations" label="All organisations" sx={{ justifySelf: "start" }} />

      {orgQuery.isLoading && <Skeleton variant="rounded" sx={{ height: 120 }} />}
      {orgQuery.isError && (
        <SupportErrorState error={orgQuery.error} resourceLabel="organisation" />
      )}

      {o && (
        <Card>
          <CardHeader
            title={o.name}
            subheader={
              [o.domain, o.slug].filter(Boolean).join(" · ") ||
              "No domain on file."
            }
            sx={{ borderBottom: 1, borderColor: "divider" }}
            titleTypographyProps={{ sx: { fontSize: 14, fontWeight: 600, letterSpacing: "-0.01em", color: "text.primary" } }}
            subheaderTypographyProps={{ sx: { fontSize: 12, color: "text.disabled" } }}
          />
          <CardContent>
            <Box
              component="dl"
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "repeat(2, 1fr)", sm: "repeat(4, 1fr)" },
                gap: 2,
                fontSize: 14,
                m: 0,
              }}
            >
              <Stat label="Contacts" value={String(o.contacts_count ?? "—")} />
              <Stat label="Open tickets" value={String(o.open_tickets_count ?? 0)} />
              <Stat label="Status" value={o.status ?? "active"} />
              <Stat
                label="Created"
                value={o.created_at ? new Date(o.created_at).toLocaleDateString() : "—"}
              />
            </Box>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader
          title="Contacts"
          sx={{ borderBottom: 1, borderColor: "divider" }}
          titleTypographyProps={{ sx: { fontSize: 14, fontWeight: 600, letterSpacing: "-0.01em", color: "text.primary" } }}
        />
        <CardContent>
          {contactsQuery.isLoading && <Skeleton variant="rounded" sx={{ height: 120 }} />}
          {contactsQuery.isError && (
            <SupportErrorState
              error={contactsQuery.error}
              resourceLabel="contacts"
            />
          )}
          {contactsQuery.data && contactsQuery.data.data.length === 0 && (
            <Typography sx={{ fontSize: 14, color: "text.disabled" }}>
              No contacts on file for this organisation.
            </Typography>
          )}
          {contactsQuery.data && contactsQuery.data.data.length > 0 && (
            <SupportTable
              head={
                <TableRow>
                  <TableCell align="left">Name</TableCell>
                  <TableCell align="left">Email</TableCell>
                  <TableCell align="left">Title</TableCell>
                  <TableCell align="left">Phone</TableCell>
                </TableRow>
              }
            >
              {contactsQuery.data.data.map((c) => (
                <TableRow
                  key={c.id}
                  sx={{ borderTop: 1, borderColor: "divider", "&:hover": { bgcolor: "action.hover" } }}
                >
                  <TableCell sx={{ fontWeight: 500 }}>
                    {[c.first_name, c.last_name].filter(Boolean).join(" ") ||
                      c.email}
                  </TableCell>
                  <TableCell>
                    <MuiLink href={`mailto:${c.email}`} color="primary.main" underline="hover">
                      {c.email}
                    </MuiLink>
                  </TableCell>
                  <TableCell sx={{ color: "text.disabled" }}>
                    {c.title ?? "—"}
                  </TableCell>
                  <TableCell sx={{ color: "text.disabled" }}>
                    {c.phone ?? "—"}
                  </TableCell>
                </TableRow>
              ))}
            </SupportTable>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader
          title="Recent tickets"
          subheader="Latest 10 tickets for this organisation."
          sx={{ borderBottom: 1, borderColor: "divider" }}
          titleTypographyProps={{ sx: { fontSize: 14, fontWeight: 600, letterSpacing: "-0.01em", color: "text.primary" } }}
          subheaderTypographyProps={{ sx: { fontSize: 12, color: "text.disabled" } }}
        />
        <CardContent>
          {ticketsQuery.isLoading && <Skeleton variant="rounded" sx={{ height: 120 }} />}
          {ticketsQuery.isError && (
            <SupportErrorState
              error={ticketsQuery.error}
              resourceLabel="tickets"
            />
          )}
          {ticketsQuery.data && ticketsQuery.data.data.length === 0 && (
            <Typography sx={{ fontSize: 14, color: "text.disabled" }}>
              This organisation has no tickets on record.
            </Typography>
          )}
          {ticketsQuery.data && ticketsQuery.data.data.length > 0 && (
            <SupportTable
              head={
                <TableRow>
                  <TableCell align="left">Ref</TableCell>
                  <TableCell align="left">Title</TableCell>
                  <TableCell align="left">Status</TableCell>
                  <TableCell align="left">Updated</TableCell>
                </TableRow>
              }
            >
              {ticketsQuery.data.data.map((t) => (
                <TableRow
                  key={t.id}
                  sx={{ borderTop: 1, borderColor: "divider", "&:hover": { bgcolor: "action.hover" } }}
                >
                  <TableCell sx={{ fontFamily: "ui-monospace, monospace", fontSize: 12 }}>{t.key}</TableCell>
                  <TableCell>
                    <MuiLink
                      component={Link}
                      to={`/support/tickets/${t.id}`}
                      color="text.primary"
                      underline="hover"
                      sx={{ fontWeight: 500 }}
                    >
                      {t.title}
                    </MuiLink>
                  </TableCell>
                  <TableCell>
                    <StatusBadge status={t.status} />
                  </TableCell>
                  <TableCell sx={{ color: "text.disabled" }}>
                    {new Date(t.updated_at).toLocaleString()}
                  </TableCell>
                </TableRow>
              ))}
            </SupportTable>
          )}
        </CardContent>
      </Card>
    </Box>
  );
}

function Stat({ label, value }: { label: string; value: string }): JSX.Element {
  return (
    <Box>
      <Box component="dt" sx={{ fontSize: 12, textTransform: "uppercase", letterSpacing: "0.05em", color: "text.disabled" }}>
        {label}
      </Box>
      <Box component="dd" sx={{ ml: 0, mt: 0.5, fontSize: "1rem", fontWeight: 500 }}>{value}</Box>
    </Box>
  );
}
